import { ProFormColumnsType } from '@ant-design/pro-components';
import { FormattedMessage } from 'react-intl';
import {
  ExpectedPropertyType,
  PropertySubType,
  PropertyTenure,
  PropertyType,
  RefinancePurpose,
} from '../../../api/btlApplicationForm/types';
import { gbPhoneNumberAntdRule } from 'utils/gbPhoneNumber';
import { Address } from '../common/Address';
import { BaseFormSchemaProps } from '../types';
import { Alert } from 'antd';

type FinancingFormSchemaProps = BaseFormSchemaProps;

export const financingFormSchema = (props: FinancingFormSchemaProps): ProFormColumnsType[] => [
  {
    title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.purpose.label' }),
    dataIndex: 'purpose',
    valueType: 'radioButton',
    fieldProps: {
      options: [
        { label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.purpose.0.label' }), value: 'PURCHASE' },
        { label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.purpose.1.label' }), value: 'REFINANCE' },
      ],
      buttonStyle: 'solid',
    },
    formItemProps: {
      rules: [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }],
    },
  },
  {
    valueType: 'dependency',
    name: ['purpose', 'refinancePurpose'],
    columns: ({ purpose, refinancePurpose }) => {
      if (purpose === 'REFINANCE') {
        return [
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.refinancePurpose.label' }),
            dataIndex: 'refinancePurpose',
            valueType: 'radioButton',
            fieldProps: {
              options: [
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.refinancePurpose.0.label' }),
                  value: 'LIKE_FOR_LIKE',
                },
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.refinancePurpose.1.label' }),
                  value: 'CAPITAL_RAISING',
                },
              ],
              buttonStyle: 'solid',
              value: refinancePurpose,
            },
            formItemProps: {
              rules: [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }],
            },
            renderFormItem: (_, { defaultRender }, form) => {
              const selected =
                form && typeof form.getFieldValue === 'function' ? form.getFieldValue('refinancePurpose') : undefined;
              return (
                <div className='relative'>
                  <div>{defaultRender(_)}</div>
                  {selected === RefinancePurpose.CAPITAL_RAISING && (
                    <div className='absolute left-72 -bottom-1/4 ml-8 mb-2 w-2/3'>
                      <Alert
                        className='p-3 pb-4'
                        description={props.formatMessage({
                          id: 'btlApplicationForm.PROPERTY.refinancePurpose.1.info',
                        })}
                        type='info'
                        showIcon
                      />
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estimatedMarketValue.label' }),
            dataIndex: 'purchasePrice',
            valueType: { type: 'money', locale: 'en-GB' },
            width: 300,
            fieldProps: {
              precision: 0,
              placeholder: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estimatedMarketValue.placeholder' }),
            },
            formItemProps: {
              hasFeedback: true,
              rules: [
                {
                  required: true,
                  message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estimatedMarketValue.error' }),
                  type: 'number',
                },
                {
                  min: 75000,
                  message: props.formatMessage({ id: 'common.error.minNumber' }, { min: '£75,000' }),
                  type: 'number',
                },
              ],
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.outstandingBalance.label' }),
            dataIndex: 'outstandingBalance',
            valueType: { type: 'money', locale: 'en-GB' },
            width: 300,
            fieldProps: form => {
              const refinancePurpose =
                form && typeof form.getFieldValue === 'function' ? form.getFieldValue('refinancePurpose') : undefined;
              return {
                precision: 0,
                placeholder: props.formatMessage({
                  id: `btlApplicationForm.FINANCING.outstandingBalance.placeholder${
                    refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE ? '.readonly' : ''
                  }`,
                }),
                value:
                  refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE
                    ? form.getFieldValue(['financingAmount'])
                    : undefined,
                // readOnly: refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE,
                disabled: refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE,
              };
            },
            formItemProps: form => {
              const refinancePurpose =
                form && typeof form.getFieldValue === 'function' ? form.getFieldValue('refinancePurpose') : undefined;
              return {
                hasFeedback: true,
                rules: [
                  {
                    required: refinancePurpose !== RefinancePurpose.LIKE_FOR_LIKE,
                    message: props.formatMessage({ id: 'btlApplicationForm.FINANCING.outstandingBalance.error' }),
                    min: 0,
                    type: 'number',
                  },
                ],
              };
            },
          },
        ];
      }
      return [
        {
          title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.purchasePrice.label' }),
          dataIndex: 'purchasePrice',
          valueType: { type: 'money', locale: 'en-GB' },
          width: 300,
          fieldProps: {
            precision: 0,
            placeholder: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.purchasePrice.placeholder' }),
          },
          formItemProps: {
            hasFeedback: true,
            rules: [
              {
                required: true,
                message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.purchasePrice.error' }),
                type: 'number',
              },
              {
                min: 75000,
                message: props.formatMessage({ id: 'common.error.minNumber' }, { min: '£75,000' }),
                type: 'number',
              },
            ],
          },
        },
      ];
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.financeAmount.label' }),
    dataIndex: 'financingAmount',
    valueType: { type: 'money', locale: 'en-GB' },
    width: 300,
    fieldProps: {
      precision: 0,
      placeholder: props.formatMessage({ id: 'btlApplicationForm.FINANCING.financeAmount.placeholder' }),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        {
          required: true,
          message: props.formatMessage({ id: 'btlApplicationForm.FINANCING.financeAmount.error' }),
          min: 0,
          type: 'number',
        },
      ],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.grossMonthlyRentalPayment.label' }),
    dataIndex: 'grossMonthlyRentalPayment',
    valueType: { type: 'money', locale: 'en-GB' },
    width: 300,
    fieldProps: {
      precision: 0,
      placeholder: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.grossMonthlyRentalPayment.placeholder' }),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        {
          required: true,
          message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.grossMonthlyRentalPayment.error' }),
          min: 0,
          type: 'number',
        },
      ],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.paymentProfile.label' }),
    dataIndex: 'paymentProfile',
    valueType: 'radioButton',
    fieldProps: {
      options: [
        {
          label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.paymentProfile.0.label' }),
          value: 'RENT_ONLY',
        },
        {
          label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.paymentProfile.1.label' }),
          value: 'ACQUISITION',
        },
      ],
      buttonStyle: 'solid',
    },
    formItemProps: {
      rules: [{ message: 'Purpose is required', required: true }],
    },
  },
  {
    valueType: 'dependency',
    name: ['paymentProfile'],
    columns: ({ paymentProfile }) => {
      if (paymentProfile === 'RENT_ONLY') {
        return [
          {
            title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.label' }),
            dataIndex: 'repaymentStrategy',
            valueType: 'select',
            fieldProps: {
              showSearch: true,
              options: [
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.0.label' }),
                  value: 'SALE_OF_INVESTMENT_PROPERTY',
                },
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.1.label' }),
                  value: 'ENDOWMENT_POLICY',
                },
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.2.label' }),
                  value: 'UK_PENSIONS',
                },
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.3.label' }),
                  value: 'REGULAR_SAVINGS_AND_INVESTMENTS',
                },
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.4.label' }),
                  value: 'STOCKS_AND_SHARES_ISA',
                },
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.5.label' }),
                  value: 'OTHER',
                },
              ],
              placeholder: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategy.placeholder' }),
            },
            formItemProps: {
              rules: [{ message: 'Repayment strategy is required', required: true }],
            },
          },
          {
            valueType: 'dependency',
            name: ['repaymentStrategy'],
            columns: ({ repaymentStrategy }) => {
              if (repaymentStrategy === 'OTHER') {
                return [
                  {
                    title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.repaymentStrategyDetails.label' }),
                    dataIndex: 'repaymentStrategyDetails',
                    valueType: 'textarea',
                    fieldProps: {
                      allowClear: false,
                    },
                    formItemProps: {
                      hasFeedback: true,
                      rules: [{ required: false }],
                    },
                  },
                ];
              }
              return [];
            },
          },
        ];
      }

      return [];
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.FINANCING.term.label' }),
    dataIndex: 'termInMonths',
    valueType: 'digit',
    width: 300,
    fieldProps: {
      precision: 0,
      placeholder: props.formatMessage({ id: 'btlApplicationForm.FINANCING.term.placeholder' }),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [
        {
          required: true,
          message: props.formatMessage({ id: 'btlApplicationForm.FINANCING.term.error' }),
          min: 5,
          max: 40,
          type: 'number',
        },
      ],
    },
    transform: value => ({ termInMonths: value * 12 }),
  },
  {
    hideInForm: props.isDipAcceptedOrLater,
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.expectedPropertyType.label' }),
    dataIndex: 'expectedPropertyType',
    valueType: 'select',
    fieldProps: {
      showSearch: true,
      options: Object.values(ExpectedPropertyType).map((type, index) => ({
        label: <FormattedMessage id={`btlApplicationForm.PROPERTY.expectedPropertyType.${index}.label`} />,
        value: type,
      })),
      placeholder: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.expectedPropertyType.placeholder' }),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }],
    },
  },
  {
    dataIndex: 'feesAddedToBalance',
    valueType: 'checkbox',
    fieldProps: {
      className: 'scale-[1.35] [&_.ant-checkbox-label]:scale-[0.75] [&_.ant-checkbox]:mr-[-16px] ml-7',
      options: [
        {
          label: props.formatMessage({ id: 'btlApplicationForm.FINANCING.term.feesAddedToBalance.label' }),
          value: true,
        },
      ],
    },
    transform: value => ({ feesAddedToBalance: value[0] ?? false }),
  },
];

export const propertyFormSchema = (props: FinancingFormSchemaProps): ProFormColumnsType[] => [
  {
    renderFormItem: () => (
      <>
        <h3>{props.formatMessage({ id: 'btlApplicationForm.PROPERTY.property.label' })}</h3>
        <Address isEditable={props.isFormEditable} />
      </>
    ),
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.tenure.label' }),
    dataIndex: 'tenure',
    valueType: 'select',
    fieldProps: {
      showSearch: true,
      options: Object.values(PropertyTenure).map((type, index) => ({
        label: <FormattedMessage id={`btlApplicationForm.PROPERTY.tenure.${index}.label`} />,
        value: type,
      })),
    },
    formItemProps: {
      className: '-mt-6',
      hasFeedback: true,
      rules: [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }],
    },
  },
  {
    valueType: 'dependency',
    name: ['tenure'],
    columns: ({ tenure }) => {
      if (tenure === PropertyTenure.LEASEHOLD) {
        return [
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.yearsRemainingOnLease.label' }),
            dataIndex: 'yearsRemainingOnLease',
            valueType: 'digit',
            width: 300,
            fieldProps: {
              precision: 0,
              allowClear: true,
              placeholder: props.formatMessage({ id: 'btlApplicationForm.FINANCING.term.placeholder' }),
            },
          },
        ];
      }
      return [];
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.propertyType.label' }),
    dataIndex: 'propertyType',
    valueType: 'select',
    fieldProps: {
      showSearch: true,
      options: Object.values(PropertyType).map((type, index) => ({
        label: <FormattedMessage id={`btlApplicationForm.PROPERTY.propertyType.${index}.label`} />,
        value: type,
      })),
      placeholder: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.propertyType.placeholder' }),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.propertySubType.label' }),
    dataIndex: 'propertySubType',
    valueType: 'select',
    fieldProps: {
      showSearch: true,
      options: Object.values(PropertySubType).map((type, index) => ({
        label: <FormattedMessage id={`btlApplicationForm.PROPERTY.propertySubType.${index}.label`} />,
        value: type,
      })),
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }],
    },
  },
  {
    valueType: 'dependency',
    name: ['propertyType'],
    columns: ({ propertyType }) => {
      if (propertyType === PropertyType.HMO) {
        return [
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.numberOfBedrooms.label' }),
            dataIndex: 'numberOfBedrooms',
            valueType: 'digit',
            width: 300,
            fieldProps: {
              precision: 0,
            },
            formItemProps: {
              hasFeedback: true,
              rules: [
                {
                  required: true,
                  message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.numberOfBedrooms.error' }),
                  min: 1,
                  type: 'number',
                },
              ],
            },
          },
          {
            dataIndex: 'hasCommunalSpace',
            valueType: 'checkbox',
            fieldProps: {
              className: 'scale-[1.35] [&_.ant-checkbox-label]:scale-[0.75] [&_.ant-checkbox]:mr-[-34px] ml-12',
              options: [
                {
                  label: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.hasCommunalSpace.label' }),
                  value: true,
                },
              ],
            },
            transform: value => ({ hasCommunalSpace: value[0] ?? false }),
          },
        ];
      } else if (propertyType === PropertyType.MUB) {
        return [
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.numberOfUnits.label' }),
            dataIndex: 'numberOfUnits',
            valueType: 'digit',
            width: 300,
            fieldProps: {
              precision: 0,
            },
            formItemProps: {
              hasFeedback: true,
              rules: [
                {
                  required: true,
                  message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.numberOfUnits.error' }),
                  min: 1,
                  type: 'number',
                },
              ],
            },
          },
        ];
      } else {
        return [];
      }
    },
  },
  {
    renderFormItem: () => (
      <>
        <h3 className={'mb-0'}>{props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estateAgent.label' })}</h3>
      </>
    ),
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estateAgentName.label' }),
    dataIndex: ['estateAgent', 'name'],
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estateAgentAgency.label' }),
    dataIndex: ['estateAgent', 'agency'],
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ required: true }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estateAgentPhoneNumber.label' }),
    dataIndex: ['estateAgent', 'phoneNumber'],
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [gbPhoneNumberAntdRule(props.formatMessage({ id: 'common.error.validPhoneNumber' })), { required: true }],
    },
  },
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.estateAgentEmail.label' }),
    dataIndex: ['estateAgent', 'emailAddress'],
    valueType: 'text',
    fieldProps: {
      allowClear: false,
    },
    formItemProps: {
      hasFeedback: true,
      rules: [{ type: 'email' }],
    },
  },
];

export const financingAndPropertyFormSchema = (props: FinancingFormSchemaProps): ProFormColumnsType[] => [
  ...financingFormSchema(props),
  {
    hideInForm: !props.isDipAcceptedOrLater,
    valueType: 'group',
    columns: propertyFormSchema(props),
  },
];
